import { Alert, App, Empty, Result, Skeleton, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import PageTitle from 'components/page-title';
import { getAccess, getOverview, postRecompute } from './api';
import CreatorDrawer from './components/creator-drawer';
import CreatorTable from './components/creator-table';
import FilterBar from './components/filter-bar';
import OverviewBody from './components/overview-body';
import { ANALYTICS_ERROR_TEXT, AccessResponse, CreatorKind, OverviewResponse } from './contract';
import { errorCode, errorText, fmtMonth, fmtNum, isMonthEnded } from './format';
import styles from './style.module.scss';

const TITLE = 'Phân tích';
const NO_DAILY = 'Tháng này chưa có file ngày. Nhập ở Doanh thu → Nhập dữ liệu.';
const NO_CREATORS = 'Phạm vi đang chọn chưa có Creator nào có số liệu trong tháng này.';

/** Không xem được (không quyền / chưa gán nhóm / chưa gắn UID / lỗi nghiệp vụ). */
const Blocked = ({ code, fallback }: { code: string | null; fallback?: string }) => (
  <Result
    status="info"
    title={
      code === 'ANALYTICS_NO_SCOPE'
        ? 'Bạn chưa được gán nhóm/Studio nào'
        : 'Chưa xem được Phân tích Creator'
    }
    subTitle={(code && ANALYTICS_ERROR_TEXT[code]) || fallback}
  />
);

/** Doanh thu → Phân tích (docs/phan-tich-creator/01-dac-ta-2026-10-06.md §8). Quyền thật do API quyết. */
const AnalyticsPage = () => {
  const { message } = App.useApp();
  const [access, setAccess] = useState<AccessResponse | null>(null);
  const [accessError, setAccessError] = useState<unknown>(null);
  const [month, setMonth] = useState('');
  const [groupId, setGroupId] = useState<number | null>(null);
  const [kind, setKind] = useState<CreatorKind | null>(null);
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<unknown>(null);
  const [drawerUid, setDrawerUid] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [recomputing, setRecomputing] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    getAccess()
      .then((res) => {
        setAccess(res);
        if (res.months.length) setMonth(res.months[0]);
      })
      .catch(setAccessError);
  }, []);

  useEffect(() => {
    if (!access?.canView || !month) return;
    const id = ++seq.current;
    setLoading(true);
    setOverviewError(null);
    getOverview({ month, groupId, kind })
      .then((res) => {
        if (id === seq.current) setOverview(res);
      })
      .catch((err) => {
        if (id === seq.current) {
          setOverview(null);
          setOverviewError(err);
        }
      })
      .finally(() => {
        if (id === seq.current) setLoading(false);
      });
  }, [access, month, groupId, kind, reloadKey]);

  const openCreator = useCallback((uid: string) => setDrawerUid(uid), []);

  const recompute = async () => {
    setRecomputing(true);
    try {
      const res = await postRecompute(month);
      message.success(
        `Đã tính lại ${fmtNum(res?.uids)} Creator của ${fmtMonth(
          res?.month || month
        ).toLowerCase()}.`
      );
      setReloadKey((k) => k + 1);
    } catch (err) {
      message.error(errorText(err, 'Tính lại không thành công.'));
    } finally {
      setRecomputing(false);
    }
  };

  if (accessError) {
    return (
      <div>
        <PageTitle title={TITLE} />
        <Blocked code={errorCode(accessError)} fallback={errorText(accessError)} />
      </div>
    );
  }
  if (!access) {
    return (
      <div>
        <PageTitle title={TITLE} />
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }
  if (!access.canView) {
    return (
      <div>
        <PageTitle title={TITLE} />
        <Blocked code={access.reason || 'ANALYTICS_NO_ACCESS'} />
      </div>
    );
  }
  if (!access.months.length) {
    return (
      <div>
        <PageTitle title={TITLE} />
        <Empty description={NO_DAILY} />
      </div>
    );
  }

  const ov = overview;
  const code = errorCode(overviewError);

  return (
    <div className={styles.page}>
      <PageTitle title={TITLE} />
      <FilterBar
        access={access}
        month={month}
        groupId={groupId}
        kind={kind}
        recomputing={recomputing}
        onMonth={setMonth}
        onGroup={setGroupId}
        onKind={setKind}
        onRecompute={recompute}
      />

      {overviewError ? (
        code ? (
          <Blocked code={code} fallback={errorText(overviewError)} />
        ) : (
          <Alert type="error" showIcon message={errorText(overviewError)} />
        )
      ) : !ov ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : ov.empty ? (
        <Empty description={NO_DAILY} />
      ) : ov.scope.creators === 0 ? (
        <Empty description={NO_CREATORS} />
      ) : (
        <Spin spinning={loading}>
          <OverviewBody ov={ov} openCreator={openCreator} />
        </Spin>
      )}

      {access.scope !== 'SELF' && !code && month && !ov?.empty && ov?.scope.creators !== 0 && (
        <CreatorTable
          month={month}
          groupId={groupId}
          kind={kind}
          reloadKey={reloadKey}
          onOpen={openCreator}
          noSeries={!!ov && !ov.seriesAvailable}
          ended={!!ov && isMonthEnded(ov.monthEnded, ov.coverage.daysRemaining)}
        />
      )}

      <CreatorDrawer uid={drawerUid} month={month} onClose={() => setDrawerUid(null)} />
    </div>
  );
};

export default AnalyticsPage;
